'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useMemo, type PropsWithChildren } from 'react'
import { parseDefinitionListChildren } from './_components/definitionListChildren'
import { DefinitionListColumn } from './_components/DefinitionListColumn.client'
import { DefinitionListItem } from './_components/DefinitionListItem.client'
import { renderAlignedRows, renderSingleColumnItems } from './_components/definitionListRender'
import { useDefinitionListRowSync } from './_hooks/useDefinitionListRowSync'
import {
  DEFINITION_LIST_SYNC_MAX_ROWS,
  useDefinitionListSync,
} from './DefinitionListProvider.client'

export type DefinitionListProps = UiComponentProps & PropsWithChildren

export function DefinitionList({
  children,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: DefinitionListProps) {
  const sync = useDefinitionListSync()
  const { columnChildren, columnCount, hasColumns, listItems } = useMemo(
    () => parseDefinitionListChildren(children),
    [children],
  )
  const visibleItems = useMemo(() => {
    if (!sync || sync.isFullDisplay) {
      return listItems
    }

    return listItems.slice(0, DEFINITION_LIST_SYNC_MAX_ROWS)
  }, [listItems, sync])
  const { isWaitingForSync, rowRefs, rowSizes } = useDefinitionListRowSync(visibleItems, sync)

  const content = hasColumns
    ? renderAlignedRows(columnChildren, columnCount)
    : renderSingleColumnItems(visibleItems, sync ? { rowRefs, rowSizes } : undefined)

  return (
    <dl
      id={id}
      aria-label={ariaLabel}
      data-testid={dataTestId ?? 'definition-list'}
      data-columns={columnCount}
      className={twMerge(
        className,
        'relative flex w-full flex-col gap-0.5',
        isWaitingForSync && 'opacity-0',
      )}
    >
      {content}
    </dl>
  )
}

DefinitionList.Item = DefinitionListItem
DefinitionList.Column = DefinitionListColumn
